import { assign, setup } from "xstate";

import { generateBackTransitionsFrom, useQuestionnaireMachine } from "~/components/questionnaire";

import { ConsideringCremation, GetStarted, GroundLevel, PurchaseForWho, GetStartedBrooklawn } from "./QuestionnaireScreens";

import data from "~/data";

export const states = {
    getStarted: {
        on: {
            NEXT: { target: "purchaseForWho" },
        },
        exit: ["searchParams.update"],
    },
    consideringCremation: {
        on: {
            BURIED: {
                target: "groundLevel",
                actions: ["setContext"],
            },
            CREMATED: {
                target: "groundLevel",
                actions: ["setContext"],
            },
            BACK: {
                target: "purchaseForWho",
            },
        },
        exit: ["searchParams.update"],
    },
    purchaseForWho: {
        on: {
            MYSELF: {
                target: "consideringCremation",
                actions: ["setContext"],
            },
            ME_AND_PARTNER: {
                target: "consideringCremation",
                actions: ["setContext"],
            },
            FAMILY_MEMBERS: {
                target: "consideringCremation",
                actions: ["setContext"],
            },
            BACK: {
                target: "getStarted",
            },
        },
        exit: ["searchParams.update"],
    },
    groundLevel: {
        on: {
            ABOVE_GROUND: {
                target: "map",
                actions: ["setContext"],
            },
            BELOW_GROUND: {
                target: "map",
                actions: ["setContext"],
            },
            BACK: {
                target: "consideringCremation",
            },
        },
        exit: ["searchParams.update"],
    },
};

// final states
states.map = {
    on: {
        BACK: generateBackTransitionsFrom(states, "map"),
    },
    entry: [
        {
            type: "packages.navigate",
            params: ({ context }) => {
                const intermentRightsValues = {
                    myself: 2,
                    "me-and-partner": 2,
                };
                const dispositions = {
                    buried: "burial",
                    cremated: "cremation",
                };
                const search = new URLSearchParams({
                    fromQuestionnaire: true,
                    groundLevel: context.groundLevel,
                    burialType: dispositions[context.disposition],
                });

                const intermentRights = intermentRightsValues[context.purchaseFor];
                if (intermentRights) {
                    search.append("intermentRights", intermentRights);
                }
                return {
                    pathname: "./map",
                    search: search.toString(),
                };
            },
        },
    ],
    exit: ["searchParams.update"],
};

const machine = setup({
    types: {} as {
        context: {
            previousState?: string;
            history: string[];
            disposition?: "buried" | "cremated";
            purchaseFor?: "myself" | "me-and-partner" | "family-members";
            groundLevel?: "above-ground" | "in-ground";
        };
    },
    actions: {
        "context.clear": assign({
            disposition: undefined,
            purchaseFor: undefined,
            groundLevel: undefined,
        }),
        setContext: assign(({ event: { type } }) => {
            // we can't rely on event params because when we "replay" the events, the params aren't part of it.
            // they are generated based on the event type.
            switch (type) {
                case "BURIED":
                    return { disposition: "buried" };
                case "CREMATED":
                    return { disposition: "cremated" };
                case "MYSELF":
                    return { purchaseFor: "myself" };
                case "ME_AND_PARTNER":
                    return { purchaseFor: "me-and-partner" };
                case "FAMILY_MEMBERS":
                    return { purchaseFor: "family-members" };
                case "ABOVE_GROUND":
                    return { groundLevel: "above-ground" };
                case "BELOW_GROUND":
                    return { groundLevel: "below-ground" };
                default:
                    return null;
            }
        }),
    },
}).createMachine({
    initial: "getStarted",
    context: {
        history: [],
        previousState: undefined,
        disposition: undefined,
        purchaseFor: undefined,
        groundLevel: undefined,
    },
    states,
});

export default function PreNeedQuestionnaire() {
    const { snapshot, send, isInitialized } = useQuestionnaireMachine(machine);

    if (!isInitialized) {
        return null;
    }

    switch (true) {
        case snapshot.matches("getStarted"):
            switch (data.flow.vendor) {
                case "brooklawn":
                    return <GetStartedBrooklawn send={send} />;
                case "greenwood":
                    return <GetStarted send={send} />;
                default:
                    return <GetStarted send={send} />;
            }

        case snapshot.matches("consideringCremation"):
            return <ConsideringCremation send={send} link={data.flow.notSureLink} />;

        case snapshot.matches("purchaseForWho"):
            return <PurchaseForWho send={send} />;

        case snapshot.matches("groundLevel"):
            return <GroundLevel send={send} />;

        default:
            return null;
    }
}
